import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import uploadimage from "../../../../public/upload-image.png";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

export default function CreateEvent() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [eventType, setEventType] = useState("single");
  const [locationType, setLocationType] = useState("venue");
  const [capacity, setCapacity] = useState(0);
  const [category, setCategory] = useState("");
  const [ticketType, setTicketType] = useState("paid");
  const [audienceType, setAudienceType] = useState("public");
  const [tags, setTags] = useState(["Prayer", "Worship", "Faithful", "Blessings"]);
  const [organizer, setOrganizer] = useState("");
  const [faqs, setFaqs] = useState([{ question: "", answer: "" }]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  
  // New states for ticket configuration
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketName, setTicketName] = useState("General Admission");
  const [ticketPrice, setTicketPrice] = useState("10.00");
  const [ticketSalesStart, setTicketSalesStart] = useState("");
  const [ticketSalesEnd, setTicketSalesEnd] = useState("");
  const [ticketStartTime, setTicketStartTime] = useState("12:00 AM");
  const [ticketEndTime, setTicketEndTime] = useState("11:59 PM");
  const [tickets, setTickets] = useState([]);
  const [ticketQuantity, setTicketQuantity] = useState(50);
  const [ticketDescription, setTicketDescription] = useState("");
  
  const categories = [
    'Sports', 
    'Conference', 
    'Food & Drink', 
    'Music Concert', 
    'Workshop', 
    'Theatre', 
    'Cooking', 
    'Health', 
    'Festival', 
    'Parties',
    'Religion',
    'Business',
    'Arts',
    'Music',
    'Other'
  ];

  useEffect(() => {
    // Get user token and details from localStorage
    const userToken = localStorage.getItem('userjwt');
    const userDetails = JSON.parse(localStorage.getItem('userdetails') || '{}');
    
    if (userToken && userDetails) {
      setToken(userToken);
      setUserId(userDetails._id);
    } else {
      Swal.fire({
        title: 'Authentication Required',
        text: 'You need to be logged in to create an event',
        icon: 'warning',
        confirmButtonText: 'Login'
      }).then(() => {
        navigate('/login');
      });
    }

    // Initialize ticket dates
    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setTicketSalesStart(today);
    setTicketSalesEnd(futureDate);

  }, [navigate]);

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImage(URL.createObjectURL(file));
    }
  };

  const addTag = (newTag) => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const addFaq = () => {
    setFaqs([...faqs, { question: "", answer: "" }]);
  };

  const updateFaq = (index, field, value) => {
    const updatedFaqs = [...faqs];
    updatedFaqs[index][field] = value;
    setFaqs(updatedFaqs);
  };

  const nextStep = () => {
    if (!title || !description || !date || !startTime || !endTime) {
      Swal.fire({
        title: 'Missing Information',
        text: 'Please fill in all required fields before proceeding',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }
    setStep(2);
  };

  const prevStep = () => {
    setStep(1);
  };

  const openTicketModal = () => {
    setShowTicketModal(true);
  };

  const closeTicketModal = () => {
    setShowTicketModal(false);
  };

  const saveTicket = () => {
    console.log('💾 Saving ticket configuration...');
    
    // Ensure price is properly formatted for paid tickets
    const formattedPrice = ticketType === 'paid' && (ticketPrice === '0.00' || !ticketPrice) ? '10.00' : ticketPrice;
    
    const newTicket = {
      name: ticketName || (ticketType === 'vip' ? 'VIP Access' : 
                          ticketType === 'early-bird' ? 'Early Bird Special' : 
                          'General Admission'),
      type: ticketType,
      price: formattedPrice,
      quantity: parseInt(ticketQuantity) || 50,
      description: ticketDescription,
      salesStart: ticketSalesStart,
      salesEnd: ticketSalesEnd,
      startTime: ticketStartTime,
      endTime: ticketEndTime
    };
    
    console.log('🎫 New ticket data:', newTicket);
    
    setTickets([...tickets, newTicket]);
    
    // Reset fields
    setTicketType('general');
    setTicketName('General Admission');
    setTicketPrice('10.00');
    setTicketQuantity(50);
    setTicketDescription('');
    
    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setTicketSalesStart(today);
    setTicketSalesEnd(futureDate);
    setTicketStartTime('12:00 AM');
    setTicketEndTime('11:59 PM');
    
    closeTicketModal();
    
    Swal.fire({
      title: 'Success',
      text: 'Ticket configuration saved!',
      icon: 'success',
      confirmButtonText: 'OK'
    });
  };

  const parseDateTime = (dateStr, timeStr) => {
    try {
      console.log(`📅 Parsing date: ${dateStr}, time: ${timeStr}`);
      
      // Handle different date formats
      let formattedDate;
      if (dateStr.includes('/')) {
        // DD/MM/YYYY or DD/MM/YY format
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          let day = parts[0];
          let month = parts[1];
          let year = parts[2];
          
          // Handle 2-digit year
          if (year.length === 2) {
            year = `20${year}`;
          }
          
          formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        } else {
          throw new Error('Invalid date format. Use DD/MM/YYYY');
        }
      } else {
        // Assume it's already in YYYY-MM-DD format
        formattedDate = dateStr;
      }

      // Parse time (handle 12-hour format with AM/PM)
      let hours, minutes;
      const timeLower = timeStr.toLowerCase();
      
      if (timeLower.includes('am') || timeLower.includes('pm')) {
        // 12-hour format
        const timeParts = timeStr.match(/(\d+):(\d+)\s*([ap]m)/i);
        if (timeParts) {
          hours = parseInt(timeParts[1]);
          minutes = parseInt(timeParts[2]);
          const period = timeParts[3].toLowerCase();
          
          if (period === 'pm' && hours < 12) hours += 12;
          if (period === 'am' && hours === 12) hours = 0;
        } else {
          throw new Error('Invalid time format. Use HH:MM AM/PM');
        }
      } else {
        // 24-hour format
        const timeParts = timeStr.split(':');
        hours = parseInt(timeParts[0]);
        minutes = parseInt(timeParts[1]);
      }

      const dateTime = new Date(formattedDate);
      dateTime.setHours(hours, minutes, 0, 0);
      
      if (isNaN(dateTime.getTime())) {
        throw new Error('Invalid date/time combination');
      }
      
      console.log(`✅ Parsed date: ${dateTime.toISOString()}`);
      return dateTime;
    } catch (error) {
      console.error('❌ Date parsing error:', error);
      throw new Error(`Date parsing failed: ${error.message}`);
    }
  };

  const createEvent = async (isDraft = false) => {
    if (!token) {
      Swal.fire({
        title: 'Authentication Required',
        text: 'You need to be logged in to create an event',
        icon: 'warning',
        confirmButtonText: 'Login'
      }).then(() => {
        navigate('/login');
      });
      return;
    }

    // Validate form if not a draft
    if (!isDraft && (!title || !description || !date || !startTime || !endTime)) {
      Swal.fire({
        title: 'Missing Information',
        text: 'Please fill in all required fields',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }
    
    // Validate tickets if not a draft
    if (!isDraft && tickets.length === 0) {
      Swal.fire({
        title: 'Missing Tickets',
        text: 'Please configure at least one ticket type for your event',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    setLoading(true);

    try {
      console.log('🚀 Starting event creation process...');
      
      // Parse dates with error handling
      let startDate, endDate;
      try {
        startDate = parseDateTime(date, startTime);
        endDate = parseDateTime(date, endTime);
        
        // Validate that end date is after start date
        if (endDate <= startDate) {
          throw new Error('End time must be after start time');
        }
      } catch (dateError) {
        console.error('❌ Date validation error:', dateError);
        Swal.fire({
          title: 'Invalid Date/Time',
          text: dateError.message,
          icon: 'error',
          confirmButtonText: 'OK'
        });
        setLoading(false);
        return;
      }

      let imageUrl = '';
      
      // Upload image to Cloudinary if exists
      if (imageFile) {
        console.log('🌅 Uploading image to Cloudinary...');
        try {
          const cloudinaryFormData = new FormData();
          cloudinaryFormData.append('file', imageFile);
          cloudinaryFormData.append('upload_preset', 'giweexpv');
          
          const cloudinaryResponse = await axios.post(
            'https://api.cloudinary.com/v1_1/dwpoik1jm/image/upload',
            cloudinaryFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 30000
            }
          );
          
          imageUrl = cloudinaryResponse.data.secure_url;
          console.log('✅ Image uploaded successfully:', imageUrl);
        } catch (imageError) {
          console.error('❌ Image upload failed:', imageError);
          // Continue without image if upload fails
          console.log('⚠️ Continuing without image due to upload failure');
        }
      }

      // Prepare event data
      const eventData = {
        userId,
        title,
        description,
        image: imageUrl,
        eventType,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        locationType,
        location: locationType === 'venue' ? location : '',
        capacity: parseInt(capacity) || 0,
        category: category.toLowerCase(),
        ticketType,
        tickets: tickets.map(ticket => ({
          ...ticket,
          quantity: parseInt(ticket.quantity) || 50,
          price: ticketType === 'paid' ? ticket.price : '0.00'
        })),
        audienceType,
        tags,
        organizer,
        faqs: faqs.filter(faq => faq.question && faq.answer),
        isDraft
      };

      console.log('📤 Sending event data to backend:');
      console.log('Event Data:', JSON.stringify(eventData, null, 2));
      console.log('User ID:', userId);
      console.log('Token exists:', !!token);

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        timeout: 30000
      };

      console.log('🌐 Making API request to backend...');
      const response = await axios.post('https://event-li-backend.onrender.com/user/event', eventData, config);

      console.log('✅ Backend response:', response.data);
      setLoading(false);

      if (response.data) {
        Swal.fire({
          title: isDraft ? 'Draft Saved' : 'Event Published',
          text: isDraft ? 'Your event has been saved as a draft' : 'Your event has been published successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate('/dashboard/event-management');
        });
      }
    } catch (error) {
      setLoading(false);
      console.error('🔥 EVENT CREATION ERROR DETAILS:');
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('❌ Backend Response Error:');
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
        console.error('Headers:', error.response.headers);
        
        const errorMessage = error.response.data?.msg || 
                           error.response.data?.errors?.[0]?.msg ||
                           error.response.data?.errors?.[0] ||
                           error.response.data?.message ||
                           'Failed to create event. Please try again.';
        
        Swal.fire({
          title: `Error (${error.response.status})`,
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else if (error.request) {
        // The request was made but no response was received
        console.error('🌐 Network Error - No response received:');
        console.error('Request:', error.request);
        
        Swal.fire({
          title: 'Network Error',
          text: 'Unable to connect to the server. Please check your internet connection and try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('⚡ Request Setup Error:', error.message);
        
        Swal.fire({
          title: 'Request Error',
          text: error.message || 'Failed to create event. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // If no tickets configured yet, open ticket modal first
    if (tickets.length === 0) {
      openTicketModal();
      return;
    }
    
    createEvent(false); // Not a draft
  };

  const handleSaveDraft = () => {
    createEvent(true); // Save as draft
  };

  // Render the ticket modal
  const renderTicketModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
          <h3 className="text-xl font-semibold mb-4">Add Tickets</h3>
          
          {/* Ticket type selection */}
          <div className="mb-6">
            <label className="block font-medium mb-2">Ticket Type</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                className={`px-3 py-2 rounded-md border ${
                  ticketType === 'vip' 
                    ? 'bg-blue-100 border-blue-400 text-blue-700' 
                    : 'border-gray-300 text-gray-700'
                }`}
                onClick={() => setTicketType('vip')}
              >
                VIP
              </button>
              <button
                type="button"
                className={`px-3 py-2 rounded-md border ${
                  ticketType === 'general' 
                    ? 'bg-blue-100 border-blue-400 text-blue-700' 
                    : 'border-gray-300 text-gray-700'
                }`}
                onClick={() => setTicketType('general')}
              >
                General
              </button>
              <button
                type="button"
                className={`px-3 py-2 rounded-md border ${
                  ticketType === 'early-bird' 
                    ? 'bg-blue-100 border-blue-400 text-blue-700' 
                    : 'border-gray-300 text-gray-700'
                }`}
                onClick={() => setTicketType('early-bird')}
              >
                Early Bird
              </button>
            </div>
          </div>

          {/* Name field */}
          <div className="mb-4">
            <label className="block font-medium mb-1">Name</label>
            <input 
              type="text" 
              value={ticketName}
              onChange={(e) => setTicketName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={ticketType === 'vip' ? 'VIP Access' : ticketType === 'early-bird' ? 'Early Bird Special' : 'General Admission'}
            />
          </div>
          
          {/* Price field */}
          <div className="mb-4">
            <label className="block font-medium mb-1">Price</label>
            <input 
              type="text" 
              value={ticketPrice}
              onChange={(e) => setTicketPrice(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="$ 0.00"
            />
            {ticketType === 'paid' && ticketPrice === '0.00' && (
              <p className="text-yellow-600 text-xs mt-1">Price will be set to $10.00 for paid tickets</p>
            )}
          </div>
          
          {/* Quantity field */}
          <div className="mb-4">
            <label className="block font-medium mb-1">Quantity Available</label>
            <input 
              type="number" 
              min="1"
              value={ticketQuantity}
              onChange={(e) => setTicketQuantity(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="50"
            />
          </div>
          
          {/* Description field */}
          <div className="mb-4">
            <label className="block font-medium mb-1">Description</label>
            <textarea
              value={ticketDescription}
              onChange={(e) => setTicketDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="What's included in this ticket?"
              rows="3"
            />
          </div>

          {/* Sales dates */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1">Sales Start</label>
              <input 
                type="date" 
                value={ticketSalesStart}
                onChange={(e) => setTicketSalesStart(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block font-medium mb-1">Sales End</label>
              <input 
                type="date" 
                value={ticketSalesEnd}
                onChange={(e) => setTicketSalesEnd(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={closeTicketModal}
              className="px-6 py-2 rounded-full border border-yellow-400 text-yellow-600 bg-white hover:bg-yellow-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveTicket}
              className="px-6 py-2 rounded-full bg-yellow-400 text-gray-800 font-semibold hover:bg-yellow-500 transition"
            >
              Save Ticket
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Display the ticket list
  const renderTicketList = () => {
    if (tickets.length === 0) return null;
    
    return (
      <div className="mb-6 p-4 border border-gray-200 rounded-lg">
        <h3 className="font-medium mb-2">Configured Tickets</h3>
        <div className="space-y-3">
          {tickets.map((ticket, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    ticket.type === 'vip' ? 'bg-purple-100 text-purple-800' :
                    ticket.type === 'early-bird' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {ticket.type.toUpperCase()}
                  </span>
                  <h4 className="font-medium mt-1">{ticket.name}</h4>
                </div>
                <div className="text-lg font-semibold">
                  ${ticket.price}
                </div>
              </div>
              
              <div className="text-sm text-gray-600 mb-2">
                {ticket.description}
              </div>
              
              <div className="flex justify-between text-sm">
                <div>
                  <span className="font-medium">Quantity:</span> {ticket.quantity}
                </div>
                <div>
                  <span className="font-medium">Sales:</span> {new Date(ticket.salesStart).toLocaleDateString()} - {new Date(ticket.salesEnd).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <button
          type="button"
          onClick={openTicketModal}
          className="mt-4 w-full py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center text-gray-700 transition"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Another Ticket
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl p-4 sm:p-6">
        {/* Progress indicator */}
        <div className="flex items-center justify-center mb-6 sm:mb-8">
          <div className="flex items-center">
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 1 ? 'bg-yellow-400 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              1
            </div>
            <div className={`w-10 sm:w-16 h-1 ${step >= 2 ? 'bg-yellow-400' : 'bg-gray-200'}`}></div>
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-yellow-400 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              2
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Image upload section */}
              <div className="relative mb-6 sm:mb-8">
                <img
                  src={
                    image ||
                    uploadimage ||
                    "https://via.placeholder.com/800x400?text=Upload+Event+Image"
                  }
                  alt="Event"
                  className="w-full h-40 sm:h-64 object-cover rounded-lg"
                />
                <label className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-40 rounded-lg cursor-pointer transition hover:bg-opacity-60">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <div className="bg-white bg-opacity-80 px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex flex-col items-center">
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6 mb-1 sm:mb-2 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12"
                      />
                    </svg>
                    <span className="text-gray-800 text-sm sm:text-base font-medium">Upload image</span>
                  </div>
                </label>
              </div>

              {/* Form */}
              <form className="space-y-4 sm:space-y-6">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">Create an event</h2>
                  <p className="text-gray-500 text-xs sm:text-sm">
                    Answer a few questions about your event and we will help you build an event page.
                  </p>
                </div>

                <div>
                  <label className="block font-medium mb-1" htmlFor="event-title">
                    What's the name of your event?
                  </label>
                  <p className="text-gray-400 text-xs mb-1 sm:mb-2">
                    Your event title will be used to help create your event's summary, description, category, and tags.
                  </p>
                  <input
                    id="event-title"
                    type="text"
                    placeholder="Event title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 sm:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>

                <div>
                  <label className="block font-medium mb-1">Event Type</label>
                  <div className="flex flex-wrap gap-4 sm:gap-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="event-type"
                        checked={eventType === "single"}
                        onChange={() => setEventType("single")}
                        className="accent-blue-600"
                      />
                      <span className="ml-2 text-sm sm:text-base">Single Event</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="event-type"
                        checked={eventType === "recurring"}
                        onChange={() => setEventType("recurring")}
                        className="accent-blue-600"
                      />
                      <span className="ml-2 text-sm sm:text-base">Recurring Event</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1" htmlFor="event-desc">
                    Event Description
                  </label>
                  <p className="text-gray-400 text-xs mb-1 sm:mb-2">
                    Catch attention with a short, engaging event description. This will appear at the top and help attendees quickly understand what your event is about (150 characters max)
                  </p>
                  <textarea
                    id="event-desc"
                    maxLength={150}
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 sm:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block font-medium mb-1">Date and time of your event</label>
                  <p className="text-gray-400 text-xs mb-2">
                    Format: Date as DD/MM/YYYY, Time as HH:MM AM/PM (e.g., 28/03/2025, 02:30 PM)
                  </p>
                  <div className="flex flex-wrap gap-2 sm:gap-4">
                    <input
                      type="text"
                      placeholder="DD/MM/YYYY"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-32 sm:w-36 border border-gray-300 rounded-md px-2 sm:px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      required
                    />
                    <input
                      type="text"
                      placeholder="02:30 PM"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-28 sm:w-32 border border-gray-300 rounded-md px-2 sm:px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      required
                    />
                    <input
                      type="text"
                      placeholder="04:30 PM"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="w-28 sm:w-32 border border-gray-300 rounded-md px-2 sm:px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-1">Event location</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setLocationType("venue")}
                      className={`px-3 sm:px-4 py-2 rounded-md text-sm sm:text-base font-medium ${
                        locationType === "venue"
                          ? "bg-[#06044B] text-white"
                          : "bg-[#F8F8FF] text-[#06044B]"
                      }`}
                    >
                      Venue
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocationType("tba")}
                      className={`px-3 sm:px-4 py-2 rounded-md text-sm sm:text-base font-medium ${
                        locationType === "tba"
                          ? "bg-[#06044B] text-white"
                          : "bg-[#F8F8FF] text-[#06044B]"
                      }`}
                    >
                      To be announced
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocationType("online")}
                      className={`px-3 sm:px-4 py-2 rounded-md text-sm sm:text-base font-medium ${
                        locationType === "online"
                          ? "bg-[#06044B] text-white"
                          : "bg-[#F8F8FF] text-[#06044B]"
                      }`}
                    >
                      Online event
                    </button>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.657 16.657L13.414 12.414a4 4 0 10-1.414 1.414l4.243 4.243a1 1 0 001.414-1.414z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Enter a location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      disabled={locationType !== "venue"}
                      required={locationType === "venue"}
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-4 sm:px-8 py-2 rounded-full border border-[#FCD20B] text-[#FCD20B] bg-white hover:bg-yellow-50 transition"
                  >
                    Exit
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-4 sm:px-8 py-2 rounded-full bg-[#FCD20B] text-[#1E1E1E] font-semibold hover:bg-yellow-500 transition flex items-center justify-center min-w-[120px]"
                    disabled={loading}
                  >
                    {loading ? (
                      <ClipLoader size={20} color="#1E1E1E" />
                    ) : (
                      "Save and continue"
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">Event details</h2>
                  <p className="text-gray-500 text-xs sm:text-sm">
                    Configure additional settings for your event.
                  </p>
                </div>

                {/* Capacity */}
                <div>
                  <label className="block font-medium mb-1">
                    What's the capacity of your event?
                  </label>
                  <p className="text-gray-400 text-xs mb-1 sm:mb-2">
                    Event capacity is the total number of tickets that you are willing to sell for your event
                  </p>
                  <div className="border border-gray-300 rounded-md p-3 sm:p-4">
                    <label className="block font-medium mb-1">Total capacity</label>
                    <input
                      type="number"
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                      className="w-full border-b border-gray-300 px-2 py-1 focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                </div>

                {/* Event category */}
                <div>
                  <label className="block font-medium mb-1">Event category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 sm:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">Choose category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat.toLowerCase()}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ticket type */}
                <div>
                  <label className="block font-medium mb-1">Ticket type</label>
                  <div className="flex flex-wrap gap-4 sm:gap-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="ticket-type"
                        checked={ticketType === "paid"}
                        onChange={() => setTicketType("paid")}
                        className="accent-blue-600"
                      />
                      <span className="ml-2 text-sm sm:text-base font-normal text-[#6D6D6D]">Paid</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="ticket-type"
                        checked={ticketType === "free"}
                        onChange={() => setTicketType("free")}
                        className="accent-blue-600"
                      />
                      <span className="ml-2 text-sm sm:text-base font-normal text-[#6D6D6D]">Free</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="ticket-type"
                        checked={ticketType === "donation"}
                        onChange={() => setTicketType("donation")}
                        className="accent-blue-600"
                      />
                      <span className="ml-2 text-sm sm:text-base font-normal text-[#6D6D6D]">Donation</span>
                    </label>
                  </div>
                </div>

                {/* Configured tickets list */}
                {renderTicketList()}

                {/* Add ticket button */}
                {tickets.length === 0 && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={openTicketModal}
                      className="px-6 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition flex items-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Configure tickets
                    </button>
                  </div>
                )}

                {/* Audience selection */}
                <div>
                  <label className="block font-medium mb-1">
                    Audience Selection (Who can view/attend this event)
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-start">
                      <input
                        type="radio"
                        name="audience"
                        checked={audienceType === "public"}
                        onChange={() => setAudienceType("public")}
                        className="accent-blue-600 mt-1"
                      />
                      <div className="ml-2">
                        <span className="text-sm sm:text-base font-normal text-[#6D6D6D]">Public (visible to everyone)</span>
                      </div>
                    </label>
                    <label className="flex items-start">
                      <input
                        type="radio"
                        name="audience"
                        checked={audienceType === "private"}
                        onChange={() => setAudienceType("private")}
                        className="accent-blue-600 mt-1"
                      />
                      <div className="ml-2">
                        <span className="text-sm sm:text-base font-normal text-[#6D6D6D]">Private (invite-only)</span>
                      </div>
                    </label>
                    <label className="flex items-start">
                      <input
                        type="radio"
                        name="audience"
                        checked={audienceType === "selected"}
                        onChange={() => setAudienceType("selected")}
                        className="accent-blue-600 mt-1"
                      />
                      <div className="ml-2">
                        <span className="text-sm sm:text-base font-normal text-[#6D6D6D]">Selected Group (choose from existing user groups, tiers, or manually add emails/usernames)</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block font-medium mb-1">Tags</label>
                  <p className="text-gray-400 text-xs mb-1 sm:mb-2">
                    Help people discover your event by adding tags related to your event
                  </p>
                  <div className="border border-gray-300 rounded-md p-3 sm:p-4">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {tags.map((tag) => (
                        <motion.div
                          key={tag}
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gray-100 px-2 sm:px-3 py-1 rounded-full flex items-center text-sm"
                        >
                          <span className="mr-1">{tag}</span>
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            ×
                          </button>
                        </motion.div>
                      ))}
                    </div>
                    <div className="flex">
                      <input
                        type="text"
                        placeholder="Add a tag"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            addTag(e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="flex-1 border-b border-gray-300 px-2 py-1 focus:outline-none focus:border-yellow-400"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.querySelector('input[placeholder="Add a tag"]');
                          if (input) {
                            addTag(input.value);
                            input.value = '';
                          }
                        }}
                        className="ml-2 px-3 py-1 bg-yellow-400 text-white rounded-md hover:bg-yellow-500 transition text-sm"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Organized by */}
                <div>
                  <label className="block font-medium mb-1">Organized by</label>
                  <p className="text-gray-400 text-xs mb-1 sm:mb-2">
                    Adding a name will create an organizer profile after publishing, and this event will appear on the organizer's profile page
                  </p>
                  <input
                    type="text"
                    value={organizer}
                    onChange={(e) => setOrganizer(e.target.value)}
                    placeholder="Organizer"
                    className="w-full border border-gray-300 rounded-md px-3 sm:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                {/* FAQs */}
                <div>
                  <label className="block font-medium mb-1">
                    Frequently asked questions
                  </label>
                  <p className="text-gray-400 text-xs mb-1 sm:mb-2">
                    Answer questions your attendees may have about the event, like accessibility and amenities
                  </p>
                  <div className="space-y-3 sm:space-y-4">
                    {faqs.map((faq, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border border-gray-300 rounded-md p-3 sm:p-4"
                      >
                        <div className="mb-2 sm:mb-3">
                          <label className="block text-sm font-medium mb-1">Question</label>
                          <input
                            type="text"
                            value={faq.question}
                            onChange={(e) => updateFaq(index, 'question', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Answer</label>
                          <textarea
                            value={faq.answer}
                            onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                            rows={2}
                            className="w-full border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </div>
                      </motion.div>
                    ))}
                    <button
                      type="button"
                      onClick={addFaq}
                      className="flex items-center text-blue-600 hover:text-blue-800 transition"
                    >
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 mr-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Add question
                    </button>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="w-full sm:w-auto px-6 sm:px-8 py-2 rounded-full border border-yellow-400 text-yellow-600 bg-white hover:bg-yellow-50 transition"
                  >
                    Back
                  </button>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      className="w-full sm:w-auto px-6 sm:px-8 py-2 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition flex items-center justify-center min-w-[120px]"
                      disabled={loading}
                    >
                      {loading ? (
                        <ClipLoader size={20} color="#1E1E1E" />
                      ) : (
                        "Save as draft"
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="w-full sm:w-auto px-6 sm:px-8 py-2 rounded-full bg-yellow-400 text-white font-semibold hover:bg-yellow-500 transition flex items-center justify-center min-w-[120px]"
                      disabled={loading}
                    >
                      {loading ? (
                        <ClipLoader size={20} color="#FFFFFF" />
                      ) : (
                        "Publish"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Ticket Modal */}
        {showTicketModal && renderTicketModal()}
      </div>
    </div>
  );
}